import React, { useState } from "react";
import { Clock, Search, AlertCircle, ArrowRight, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function OutstandingOpenPosReport({ pos }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const getDaysSinceUpdate = (po) => {
    const lastUpdatedStr = po.updatedAt || po.date || po.createdAt || new Date().toISOString();
    const lastUpdated = new Date(lastUpdatedStr);
    const today = new Date();
    const diffMs = today - lastUpdated;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  // Filter pos: status === "Bid Submitted" and days outstanding >= 21
  const outstandingPOs = pos.filter(po => {
    if (po.status !== "Bid Submitted") return false;
    
    const days = getDaysSinceUpdate(po);
    if (days < 21) return false;

    const matchesSearch = 
      (po.poNumber || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.clientName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (po.items || []).some(item => (item.staff || "").toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const getStaffInitials = (staffName) => {
    if (!staffName) return "??";
    return staffName.substring(0, 2).toUpperCase();
  };

  return (
    <div className="card glass-card animate-fade-in" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "1.25rem", fontWeight: 600, color: "var(--text-primary)" }}>
            <Clock size={20} className="text-secondary" style={{ color: "var(--primary)" }} />
            Outstanding Open POs
          </h3>
          <p className="text-secondary" style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem" }}>
            List of open bids (Bid Submitted status) that have not been updated or updated in 21+ calendar days.
          </p>
        </div>

        <div className="input-with-icon" style={{ minWidth: "250px", margin: 0 }}>
          <Search size={16} className="input-icon" />
          <input
            type="text"
            className="form-input icon-padding"
            placeholder="Search by PO #, title, staff..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>
      </div>

      <div className="table-responsive" style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: "8px" }}>
        <table className="reports-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "rgba(255, 255, 255, 0.01)", borderBottom: "1px solid var(--border-color)" }}>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600 }}>PO Number</th>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600 }}>Project / Title</th>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600 }}>Client Name</th>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600, textAlign: "center" }}>Days Idle</th>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600 }}>Procurement Staff</th>
              <th style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", fontWeight: 600, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {outstandingPOs.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "3rem", fontStyle: "italic", color: "var(--text-muted)" }}>
                  No outstanding open POs found.
                </td>
              </tr>
            ) : (
              outstandingPOs.map(po => {
                const daysIdle = getDaysSinceUpdate(po);
                // Unique staff assigned
                const staffList = [...new Set((po.items || []).map(item => item.staff).filter(Boolean))];

                return (
                  <tr key={po.id} style={{ borderBottom: "1px solid var(--border-color)", transition: "background-color 0.2s" }}>
                    <td style={{ padding: "1rem", fontWeight: 600, color: "var(--text-primary)" }}>{po.poNumber}</td>
                    <td style={{ padding: "1rem" }}>
                      <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{po.title || "Untitled PO"}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                        Submitted on {po.date || "N/A"}
                      </div>
                    </td>
                    <td style={{ padding: "1rem", color: "var(--text-secondary)" }}>{po.clientName}</td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <span className="badge" style={{ 
                        backgroundColor: daysIdle >= 30 ? "rgba(239, 68, 68, 0.15)" : "rgba(245, 158, 11, 0.15)", 
                        color: daysIdle >= 30 ? "#EF4444" : "#F59E0B",
                        border: daysIdle >= 30 ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(245, 158, 11, 0.3)",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        fontWeight: 600
                      }}>
                        {daysIdle} Days
                      </span>
                    </td>
                    <td style={{ padding: "1rem" }}>
                      {staffList.length > 0 ? (
                        <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                          {staffList.map(staff => (
                            <span 
                              key={staff} 
                              className="badge badge-secondary" 
                              style={{ 
                                display: "inline-flex", 
                                alignItems: "center", 
                                gap: "0.25rem",
                                fontSize: "0.75rem",
                                padding: "0.2rem 0.4rem"
                              }}
                            >
                              <User size={10} /> {staff}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.8rem", fontStyle: "italic", color: "var(--text-muted)" }}>None</span>
                      )}
                    </td>
                    <td style={{ padding: "1rem", textAlign: "right" }}>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => navigate("/po", { state: { openPoId: po.id } })}
                        style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                      >
                        View PO <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
